import { useEffect, useState } from 'react'
import { getUsers, getClients, createUser, updateUser, deleteUser } from '../../services/api'
import { User, Client } from '../../types'
import { Plus, Users, Search, Trash2, Edit2, Shield, Eye, EyeOff } from 'lucide-react'

export default function UserManager() {
  const [users, setUsers] = useState<User[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [clientFilter, setClientFilter] = useState('')
  const [showNew, setShowNew] = useState(false)
  const [showEdit, setShowEdit] = useState<number | null>(null)
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    Promise.all([getUsers(), getClients()])
      .then(([userRes, clientRes]) => {
        setUsers(userRes.data.users)
        setClients(clientRes.data.clients)
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const handleCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget
    const data = new FormData(form)

    const payload: any = {
      email: data.get('email') as string,
      password: data.get('password') as string,
      first_name: data.get('first_name') as string,
      last_name: data.get('last_name') as string,
      role: data.get('role') as string,
    }

    const clientId = data.get('client_id')
    if (clientId) payload.client_id = parseInt(clientId as string)

    try {
      const res = await createUser(payload)
      setUsers([res.data, ...users])
      setShowNew(false)
      form.reset()
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create user')
    }
  }

  const handleUpdate = async (e: React.FormEvent<HTMLFormElement>, userId: number) => {
    e.preventDefault()
    const form = e.currentTarget
    const data = new FormData(form)

    const payload: any = {}
    const firstName = data.get('first_name')
    const lastName = data.get('last_name')
    const role = data.get('role')
    const isActive = data.get('is_active')
    const clientId = data.get('client_id')

    if (firstName) payload.first_name = firstName
    if (lastName) payload.last_name = lastName
    if (role) payload.role = role
    if (isActive !== null) payload.is_active = isActive === 'true'
    if (clientId) payload.client_id = parseInt(clientId as string)

    try {
      await updateUser(userId, payload)
      const updated = users.map(u => u.id === userId ? { ...u, ...payload } : u)
      setUsers(updated)
      setShowEdit(null)
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update user')
    }
  }

  const handleDelete = async (userId: number) => {
    if (!confirm('Are you sure you want to deactivate this user?')) return
    try {
      await deleteUser(userId)
      setUsers(users.filter(u => u.id !== userId))
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete user')
    }
  }

  const filtered = users.filter(u => {
    const matchesSearch = !filter || 
      u.email.toLowerCase().includes(filter.toLowerCase()) ||
      (u.first_name && u.first_name.toLowerCase().includes(filter.toLowerCase())) ||
      (u.last_name && u.last_name.toLowerCase().includes(filter.toLowerCase()))
    const matchesRole = !roleFilter || u.role === roleFilter
    const matchesClient = !clientFilter || u.client_id?.toString() === clientFilter
    return matchesSearch && matchesRole && matchesClient
  })

  const roleColors: Record<string, string> = {
    superadmin: 'bg-purple-50 text-purple-600 border-purple-200',
    admin: 'bg-red-50 text-red-600 border-red-200',
    staff: 'bg-blue-50 text-blue-600 border-blue-200',
    client_admin: 'bg-orange-50 text-orange-600 border-orange-200',
    client_viewer: 'bg-gray-50 text-gray-600 border-gray-200',
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-navy-800">User Management</h2>
        <button onClick={() => setShowNew(true)} className="btn-primary flex items-center gap-2">
          <Plus size={18} />
          New User
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="input pl-9"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="input w-40"
          >
            <option value="">All Roles</option>
            <option value="superadmin">Superadmin</option>
            <option value="admin">Admin</option>
            <option value="staff">Staff</option>
            <option value="client_admin">Client Admin</option>
            <option value="client_viewer">Client Viewer</option>
          </select>
          <select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            className="input w-48"
          >
            <option value="">All Clients</option>
            <option value="null">Internal (No Client)</option>
            {clients.map(c => (
              <option key={c.id} value={c.id.toString()}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">User</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Role</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Client</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(user => (
                <tr key={user.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-navy-100 flex items-center justify-center text-navy-700 font-semibold text-sm">
                        {user.first_name?.[0]}{user.last_name?.[0]}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-navy-800">
                          {user.first_name} {user.last_name}
                        </p>
                        <p className="text-xs text-gray-400">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${roleColors[user.role] || 'bg-gray-50 text-gray-600'}`}>
                      <Shield size={10} className="mr-1" />
                      {user.role.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-500">
                    {user.client_name || (
                      <span className="text-gray-300 italic">Internal</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                      user.is_active ? 'bg-green-50 text-green-600' : 'bg-gray-50 text-gray-500'
                    }`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setShowEdit(user.id)}
                        className="p-1.5 text-navy-400 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="p-1.5 text-navy-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="Deactivate"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <Users size={48} className="mx-auto mb-4 opacity-50" />
            <p>No users found matching your filters.</p>
          </div>
        )}
      </div>

      {/* New User Modal */}
      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-navy-800 mb-4">Create New User</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">First Name</label>
                  <input name="first_name" type="text" className="input" required />
                </div>
                <div>
                  <label className="label">Last Name</label>
                  <input name="last_name" type="text" className="input" required />
                </div>
              </div>
              <div>
                <label className="label">Email</label>
                <input name="email" type="email" className="input" required />
              </div>
              <div>
                <label className="label">Password</label>
                <div className="relative">
                  <input
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    className="input pr-10"
                    minLength={8}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">Minimum 8 characters</p>
              </div>
              <div>
                <label className="label">Role</label>
                <select name="role" className="input" required>
                  <option value="">Select role...</option>
                  <option value="admin">Admin</option>
                  <option value="staff">Staff</option>
                  <option value="client_admin">Client Admin</option>
                  <option value="client_viewer">Client Viewer</option>
                </select>
              </div>
              <div>
                <label className="label">Client Company</label>
                <select name="client_id" className="input">
                  <option value="">Internal User (No Client)</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">Required for Client Admin and Client Viewer roles</p>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowNew(false)} className="btn-outline flex-1">
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1">
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            {(() => {
              const user = users.find(u => u.id === showEdit)
              if (!user) return null
              return (
                <>
                  <h3 className="text-lg font-semibold text-navy-800 mb-4">Edit User</h3>
                  <form onSubmit={(e) => handleUpdate(e, user.id)} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label">First Name</label>
                        <input name="first_name" type="text" className="input" defaultValue={user.first_name || ''} />
                      </div>
                      <div>
                        <label className="label">Last Name</label>
                        <input name="last_name" type="text" className="input" defaultValue={user.last_name || ''} />
                      </div>
                    </div>
                    <div>
                      <label className="label">Role</label>
                      <select name="role" className="input" defaultValue={user.role}>
                        <option value="admin">Admin</option>
                        <option value="staff">Staff</option>
                        <option value="client_admin">Client Admin</option>
                        <option value="client_viewer">Client Viewer</option>
                      </select>
                    </div>
                    <div>
                      <label className="label">Client Company</label>
                      <select name="client_id" className="input" defaultValue={user.client_id || ''}>
                        <option value="">Internal User</option>
                        {clients.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="label">Status</label>
                      <select name="is_active" className="input" defaultValue={user.is_active ? 'true' : 'false'}>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                    <div className="flex gap-3 pt-2">
                      <button type="button" onClick={() => setShowEdit(null)} className="btn-outline flex-1">
                        Cancel
                      </button>
                      <button type="submit" className="btn-primary flex-1">
                        Save Changes
                      </button>
                    </div>
                  </form>
                </>
              )
            })()}
          </div>
        </div>
      )}
    </div>
  )
}
